import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const API = "/backend/public/api";
const MAX_ATTACHMENT_SIZE = 2 * 1024 * 1024;

const shell = {
  display: "grid",
  gridTemplateColumns: "minmax(260px, 340px) minmax(0, 1fr)",
  height: "calc(100vh - 210px)",
  minHeight: "520px",
  maxHeight: "760px",
  background: "rgba(255,255,255,0.96)",
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: "18px",
  overflow: "hidden",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
};

const sidebar = {
  borderRight: "1px solid rgba(148, 163, 184, 0.18)",
  background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 48%, #f8fafc 100%)",
  minWidth: 0,
  minHeight: 0,
  overflow: "hidden",
  display: "grid",
  gridTemplateRows: "auto 1fr",
};

const chatArea = {
  display: "grid",
  gridTemplateRows: "auto 1fr auto",
  minWidth: 0,
  minHeight: 0,
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
};

const iconButton = {
  width: "38px",
  height: "38px",
  borderRadius: "12px",
  border: "1px solid rgba(124, 45, 58, 0.2)",
  background: "#fff",
  color: "#7c2d3a",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const primaryButton = {
  border: "none",
  borderRadius: "12px",
  background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
  color: "#fff",
  fontWeight: 800,
  padding: "10px 14px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
};

function actorParams(actor) {
  return {
    actor_type: actor?.type || "",
    actor_id: actor?.id || 0,
    actor_name: actor?.name || "",
  };
}

function initials(name) {
  return (name || "U").trim().slice(0, 1).toUpperCase();
}

function typeLabel(type) {
  if (type === "admin") return "Admin";
  if (type === "franchise") return "Franchise";
  return "Student";
}

function formatTime(value) {
  if (!value) return "";
  const rawValue = String(value);
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(rawValue);
  const normalizedValue = hasTimezone ? rawValue : `${rawValue.replace(" ", "T")}Z`;
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  });
}

function formatFileSize(size) {
  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function getCategories(actorType) {
  if (actorType === "admin") {
    return [
      { key: "admin", label: "Admin" },
      { key: "franchise", label: "Franchise" },
      { key: "student", label: "Student" },
    ];
  }

  if (actorType === "franchise") {
    return [
      { key: "admin", label: "Admin" },
      { key: "student", label: "Own Students" },
    ];
  }

  return [{ key: "franchise", label: "My Franchise" }];
}

export default function DirectChat({ actor, title = "New Message" }) {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const userSelectedRef = useRef(false);
  const [messages, setMessages] = useState([]);
  const [threadSearch, setThreadSearch] = useState("");
  const [participantSearch, setParticipantSearch] = useState("");
  const [participants, setParticipants] = useState([]);
  const [participantCategory, setParticipantCategory] = useState(() => getCategories(actor?.type)[0]?.key || "");
  const [showPicker, setShowPicker] = useState(false);
  const [draft, setDraft] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState("");
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const lastThreadSearchRef = useRef("");

  const categories = useMemo(() => getCategories(actor?.type), [actor?.type]);
  const ready = Boolean(actor?.type && actor?.id !== undefined && actor?.id !== null);

  const loadThreads = useCallback(async () => {
    if (!ready) return;
    const res = await axios.get(`${API}/direct-chat/threads`, {
      params: { ...actorParams(actor), search: threadSearch, _ts: Date.now() },
    });
    const rows = (res.data?.threads || []).map((row) => ({ ...row, id: Number(row.id) }));
    const normalizedSearch = threadSearch.trim().toLowerCase();
    const searchChanged = lastThreadSearchRef.current !== normalizedSearch;
    lastThreadSearchRef.current = normalizedSearch;

    setThreads((previousRows) => {
      const updatedById = new Map(rows.map((row) => [Number(row.id), row]));
      const nextRows = searchChanged || previousRows.length === 0
        ? rows
        : [
            ...previousRows
              .filter((row) => updatedById.has(Number(row.id)))
              .map((row) => updatedById.get(Number(row.id)) || row),
            ...rows.filter((row) => !previousRows.some((previous) => Number(previous.id) === Number(row.id))),
          ];

      setSelectedThread((current) => {
        if (current && nextRows.some((row) => Number(row.id) === Number(current.id))) {
          return nextRows.find((row) => Number(row.id) === Number(current.id)) || current;
        }
        if (!userSelectedRef.current && !normalizedSearch) {
          return nextRows[0] || null;
        }
        return null;
      });

      return nextRows;
    });
  }, [actor, ready, threadSearch]);

  const loadMessages = useCallback(async () => {
    if (!ready || !selectedThread?.id) {
      setMessages([]);
      return;
    }
    const res = await axios.get(`${API}/direct-chat/threads/${selectedThread.id}/messages`, {
      params: { ...actorParams(actor), _ts: Date.now() },
    });
    setMessages(res.data?.messages || []);
  }, [actor, ready, selectedThread?.id]);

  const loadParticipants = useCallback(async () => {
    if (!ready || !showPicker) return;
    const res = await axios.get(`${API}/direct-chat/participants`, {
      params: {
        ...actorParams(actor),
        category: participantCategory,
        search: participantSearch,
        _ts: Date.now(),
      },
    });
    setParticipants(res.data?.participants || []);
  }, [actor, participantCategory, participantSearch, ready, showPicker]);

  useEffect(() => {
    setLoading(true);
    loadThreads()
      .catch((error) => setNotice(error.response?.data?.message || "Chat list load failed"))
      .finally(() => setLoading(false));
  }, [loadThreads]);

  useEffect(() => {
    loadMessages().catch((error) => setNotice(error.response?.data?.message || "Message load failed"));
  }, [loadMessages]);

  useEffect(() => {
    loadParticipants().catch((error) => setNotice(error.response?.data?.message || "Participant load failed"));
  }, [loadParticipants]);

  useEffect(() => {
    if (!ready) return undefined;
    const timer = setInterval(() => {
      loadMessages().catch(() => {});
    }, 8000);
    return () => clearInterval(timer);
  }, [loadMessages, ready]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, selectedThread?.id]);

  const startChat = async (participant) => {
    try {
      const res = await axios.post(`${API}/direct-chat/threads`, {
        ...actorParams(actor),
        target_type: participant.type,
        target_id: participant.id,
        target_name: participant.name,
      });
      setShowPicker(false);
      setParticipantSearch("");
      userSelectedRef.current = true;
      setSelectedThread(res.data?.thread ? { ...res.data.thread, id: Number(res.data.thread.id) } : null);
      await loadThreads();
    } catch (error) {
      setNotice(error.response?.data?.message || "Chat start failed");
    }
  };

  const handleAttachment = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setAttachment(null);
      return;
    }
    if (file.size > MAX_ATTACHMENT_SIZE) {
      setNotice("Attachment size must be within 2 MB");
      event.target.value = "";
      setAttachment(null);
      return;
    }
    setAttachment(file);
  };

  const sendMessage = async () => {
    if (!selectedThread?.id || sending) return;
    if (!draft.trim() && !attachment) {
      setNotice("Type a message or choose an attachment");
      return;
    }

    const form = new FormData();
    Object.entries(actorParams(actor)).forEach(([key, value]) => form.append(key, value));
    form.append("message", draft.trim());
    if (attachment) {
      form.append("attachment", attachment);
    }

    try {
      setSending(true);
      setNotice("");
      await axios.post(`${API}/direct-chat/threads/${selectedThread.id}/messages`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDraft("");
      setAttachment(null);
      if (fileRef.current) {
        fileRef.current.value = "";
      }
      await loadMessages();
      await loadThreads();
    } catch (error) {
      setNotice(error.response?.data?.message || "Message send failed");
    } finally {
      setSending(false);
    }
  };

  if (!ready) {
    return (
      <div style={{ padding: "22px", background: "#fff", borderRadius: "16px", color: "#b91c1c", fontWeight: 800 }}>
        Chat profile not found. Please login again.
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between", gap: "14px", alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: 0, color: "#0f172a" }}>{title}</h3>
          <p style={{ margin: "6px 0 0", color: "#64748b" }}>Direct chat with permitted users, images, and attachments within 2 MB.</p>
        </div>
        <span style={{ color: "#7c2d3a", fontWeight: 800 }}>{typeLabel(actor.type)}: {actor.name || "User"}</span>
      </div>

      {notice ? (
        <div style={{ marginBottom: "12px", padding: "10px 12px", borderRadius: "12px", background: "#fff7ed", color: "#9a3412", fontWeight: 800 }}>
          {notice}
        </div>
      ) : null}

      <div className="direct-chat-shell" style={shell}>
        <div style={sidebar}>
          <div style={{ padding: "16px", borderBottom: "1px solid rgba(148, 163, 184, 0.18)" }}>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <div style={{ position: "relative", flex: 1 }}>
                <i className="bi bi-search" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}></i>
                <input
                  className="form-control"
                  value={threadSearch}
                  onChange={(event) => setThreadSearch(event.target.value)}
                  placeholder="Search chats"
                  style={{ paddingLeft: "36px", borderRadius: "12px", borderColor: "rgba(148, 163, 184, 0.28)" }}
                />
              </div>
              <button type="button" style={iconButton} onClick={() => setShowPicker(true)} title="Start new chat">
                <i className="bi bi-plus-lg"></i>
              </button>
            </div>
          </div>

          <div style={{ minHeight: 0, overflowY: "auto" }}>
            {loading ? <div style={{ padding: "18px", color: "#64748b", fontWeight: 700 }}>Loading chats...</div> : null}
            {!loading && threads.length === 0 ? (
              <div style={{ padding: "18px", color: "#64748b", lineHeight: 1.6 }}>No chats yet. Use the plus button to start a permitted conversation.</div>
            ) : null}
            {threads.map((thread) => {
              const active = selectedThread?.id === thread.id;
              return (
                <button
                  key={thread.id}
                  type="button"
                  onClick={() => {
                    userSelectedRef.current = true;
                    setSelectedThread(thread);
                  }}
                  style={{
                    width: "100%",
                    border: "none",
                    borderBottom: "1px solid rgba(148, 163, 184, 0.12)",
                    background: active ? "rgba(124, 45, 58, 0.1)" : "transparent",
                    padding: "13px 16px",
                    textAlign: "left",
                    display: "flex",
                    gap: "12px",
                    alignItems: "center",
                  }}
                >
                  <span style={{ width: "42px", height: "42px", borderRadius: "14px", background: active ? "#7c2d3a" : "#fff", color: active ? "#fff" : "#7c2d3a", border: "1px solid rgba(124,45,58,0.18)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900 }}>
                    {initials(thread.other_name)}
                  </span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <strong style={{ display: "block", color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{thread.other_name}</strong>
                    <span style={{ display: "block", color: "#64748b", fontSize: "12px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {thread.last_message || typeLabel(thread.other_type)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div style={chatArea}>
          {selectedThread ? (
            <>
              <div style={{ padding: "16px 18px", borderBottom: "1px solid rgba(148, 163, 184, 0.18)", display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center" }}>
                <div style={{ display: "flex", gap: "12px", alignItems: "center", minWidth: 0 }}>
                  <span style={{ width: "44px", height: "44px", borderRadius: "15px", background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900 }}>
                    {initials(selectedThread.other_name)}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <h5 style={{ margin: 0, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selectedThread.other_name}</h5>
                    <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: "13px" }}>{typeLabel(selectedThread.other_type)}</p>
                  </div>
                </div>
                <button type="button" style={iconButton} onClick={() => loadMessages().catch(() => {})} title="Refresh chat">
                  <i className="bi bi-arrow-clockwise"></i>
                </button>
              </div>

              <div style={{ padding: "18px", overflowY: "auto", minHeight: 0, overscrollBehavior: "contain" }}>
                {messages.map((message) => {
                  const mine = message.sender_type === actor.type && Number(message.sender_id) === Number(actor.id);
                  const imageAttachment = message.attachment_type?.startsWith("image/");
                  return (
                    <div key={message.id} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", marginBottom: "12px" }}>
                      <div style={{ maxWidth: "min(620px, 78%)", background: mine ? "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)" : "#fff", color: mine ? "#fff" : "#0f172a", border: mine ? "none" : "1px solid rgba(148, 163, 184, 0.18)", borderRadius: mine ? "18px 18px 4px 18px" : "18px 18px 18px 4px", padding: "10px 12px", boxShadow: "0 12px 22px rgba(15, 23, 42, 0.08)" }}>
                        {!mine ? <strong style={{ display: "block", fontSize: "12px", marginBottom: "4px", color: "#7c2d3a" }}>{message.sender_name}</strong> : null}
                        {message.message ? <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.55 }}>{message.message}</div> : null}
                        {message.attachment_url ? (
                          <div style={{ marginTop: message.message ? "8px" : 0 }}>
                            {imageAttachment ? (
                              <a href={message.attachment_url} target="_blank" rel="noreferrer">
                                <img src={message.attachment_url} alt={message.attachment_name || "Attachment"} style={{ display: "block", maxWidth: "260px", maxHeight: "190px", objectFit: "cover", borderRadius: "12px", border: mine ? "1px solid rgba(255,255,255,0.34)" : "1px solid rgba(148,163,184,0.2)" }} />
                              </a>
                            ) : (
                              <a href={message.attachment_url} target="_blank" rel="noreferrer" style={{ color: mine ? "#fff7ed" : "#7c2d3a", fontWeight: 800, textDecoration: "none" }}>
                                <i className="bi bi-paperclip"></i> {message.attachment_name || "Attachment"} {formatFileSize(message.attachment_size)}
                              </a>
                            )}
                          </div>
                        ) : null}
                        <span style={{ display: "block", marginTop: "5px", fontSize: "11px", color: mine ? "rgba(255,255,255,0.76)" : "#94a3b8", textAlign: mine ? "right" : "left" }}>{formatTime(message.created_at)}</span>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              <div style={{ padding: "14px 16px", borderTop: "1px solid rgba(148, 163, 184, 0.18)", background: "#fff" }}>
                {attachment ? (
                  <div style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px", color: "#475569", fontWeight: 700 }}>
                    <i className="bi bi-paperclip"></i>
                    <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{attachment.name} ({formatFileSize(attachment.size)})</span>
                    <button type="button" className="btn btn-sm btn-light" onClick={() => { setAttachment(null); if (fileRef.current) fileRef.current.value = ""; }}>
                      Remove
                    </button>
                  </div>
                ) : null}
                <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
                  <input ref={fileRef} type="file" onChange={handleAttachment} style={{ display: "none" }} />
                  <button type="button" style={iconButton} onClick={() => fileRef.current?.click()} title="Attach image or file">
                    <i className="bi bi-paperclip"></i>
                  </button>
                  <textarea
                    className="form-control"
                    rows="1"
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        sendMessage();
                      }
                    }}
                    placeholder="Write a message"
                    style={{ resize: "none", minHeight: "42px", borderRadius: "12px" }}
                  />
                  <button type="button" style={primaryButton} onClick={sendMessage} disabled={sending}>
                    <i className="bi bi-send-fill"></i>
                    {sending ? "Sending" : "Send"}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div style={{ padding: "34px", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", textAlign: "center" }}>
              Select a chat from the left side or use plus button to start a permitted conversation.
            </div>
          )}
        </div>
      </div>

      {showPicker ? (
        <div onClick={() => setShowPicker(false)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.34)", backdropFilter: "blur(4px)", zIndex: 4000, display: "flex", alignItems: "center", justifyContent: "center", padding: "18px" }}>
          <div onClick={(event) => event.stopPropagation()} style={{ width: "min(720px, 100%)", maxHeight: "84vh", overflow: "hidden", background: "#fff", borderRadius: "18px", boxShadow: "0 28px 60px rgba(15, 23, 42, 0.24)", display: "grid", gridTemplateRows: "auto auto 1fr" }}>
            <div style={{ padding: "18px", borderBottom: "1px solid rgba(148, 163, 184, 0.18)", display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center" }}>
              <div>
                <h4 style={{ margin: 0, color: "#0f172a" }}>Start New Chat</h4>
                <p style={{ margin: "4px 0 0", color: "#64748b" }}>Only permitted users are shown here.</p>
              </div>
              <button type="button" style={iconButton} onClick={() => setShowPicker(false)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid rgba(148, 163, 184, 0.14)" }}>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
                {categories.map((category) => (
                  <button
                    key={category.key}
                    type="button"
                    onClick={() => setParticipantCategory(category.key)}
                    style={{
                      border: "1px solid rgba(124,45,58,0.22)",
                      borderRadius: "999px",
                      padding: "8px 13px",
                      background: participantCategory === category.key ? "#7c2d3a" : "#fff",
                      color: participantCategory === category.key ? "#fff" : "#7c2d3a",
                      fontWeight: 800,
                    }}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
              <input className="form-control" value={participantSearch} onChange={(event) => setParticipantSearch(event.target.value)} placeholder="Search by name, ID, mobile" style={{ borderRadius: "12px" }} />
            </div>
            <div style={{ overflowY: "auto", padding: "8px 0" }}>
              {participants.length === 0 ? <div style={{ padding: "18px", color: "#64748b" }}>No permitted users found.</div> : null}
              {participants.map((participant) => (
                <button key={`${participant.type}-${participant.id}`} type="button" onClick={() => startChat(participant)} style={{ width: "100%", border: "none", borderBottom: "1px solid rgba(148, 163, 184, 0.12)", background: "#fff", padding: "13px 18px", display: "flex", alignItems: "center", gap: "12px", textAlign: "left" }}>
                  <span style={{ width: "42px", height: "42px", borderRadius: "14px", background: "#fff7ed", color: "#7c2d3a", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900 }}>
                    {initials(participant.name)}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <strong style={{ color: "#0f172a", display: "block" }}>{participant.name}</strong>
                    <span style={{ color: "#64748b", fontSize: "13px" }}>{typeLabel(participant.type)} {participant.subtitle ? `| ${participant.subtitle}` : ""}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <style>
        {`
          .direct-chat-shell {
            height: calc(100vh - 210px);
            min-height: 520px;
            max-height: 760px;
          }
          @media (max-width: 860px) {
            .direct-chat-shell {
              grid-template-columns: 1fr !important;
              grid-template-rows: minmax(220px, 38vh) minmax(360px, 1fr);
              height: calc(100vh - 150px) !important;
              min-height: 620px !important;
              max-height: none !important;
            }
            .direct-chat-shell > div:first-child {
              border-right: none !important;
              border-bottom: 1px solid rgba(148, 163, 184, 0.18);
              min-height: 0;
              overflow: hidden;
            }
          }
          @media (max-width: 560px) {
            .direct-chat-shell {
              height: calc(100vh - 128px) !important;
              min-height: 600px !important;
              border-radius: 14px !important;
            }
            .direct-chat-shell textarea.form-control {
              min-width: 0;
            }
          }
        `}
      </style>
    </div>
  );
}



